"use client";

import { START_PROCESS_HYPE_PRIZES_MUTATION } from "@/graphql/mutations/uploadMutations";
import { JOB_STATUS_QUERY } from "@/graphql/queries/uploadQueries";
import { uploadZipMultipart } from "@/lib/multipartUpload";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { FiUploadCloud } from "react-icons/fi";

export default function UploadHypePrizesPage() {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [deleteExisting, setDeleteExisting] = useState(false);

    const [uploading, setUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);

    const [taskId, setTaskId] = useState<string | null>(null);
    const [processingPct, setProcessingPct] = useState(0);

    const client = useApolloClient();
    const [startProcess] = useMutation(START_PROCESS_HYPE_PRIZES_MUTATION);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        setFile(e.target.files?.[0] || null);

    const clearFileInput = () => {
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleClick = () => fileInputRef.current?.click();

    const handleUpload = async () => {
        if (!file) return;

        setUploading(true);
        setUploadPct(0);
        setProcessingPct(0);
        setTaskId(null);

        try {
            // 1) Upload to Spaces with multipart + live progress
            const { key } = await uploadZipMultipart(
                client,
                file,
                (done, total) => setUploadPct(Math.round((done / total) * 100)),
                4,
                "hype_prizes",
            );

            // 2) Kick off backend processing task
            const res = await startProcess({
                variables: { key, deleteExisting },
            });
            setTaskId(res.data.startProcessHypePrizesExcel.taskId);
        } catch (err) {
            console.error(err);
        } finally {
            setUploading(false);
            clearFileInput();
        }
    };

    // Poll job status while task is active
    const {
        data: statusData,
        startPolling,
        stopPolling,
    } = useQuery(JOB_STATUS_QUERY, {
        variables: { taskId },
        skip: !taskId,
        pollInterval: 1500,
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (!taskId) return;
        startPolling(1500);
        return () => stopPolling();
    }, [taskId, startPolling, stopPolling]);

    useEffect(() => {
        const s = statusData?.jobStatus;
        if (!s) return;
        if (typeof s.percent === "number") setProcessingPct(s.percent);
        if (s.state === "SUCCESS" || s.state === "FAILURE") {
            stopPolling();
        }
    }, [statusData, stopPolling]);

    const job = statusData?.jobStatus;
    const finished = job?.state === "SUCCESS" || job?.state === "FAILURE";

    const processedFallback =
        job?.processed ??
        (job?.result
            ? (job.result.successful?.length || 0) +
              (job.result.failed?.length || 0) +
              (job.result.skipped?.length || 0)
            : 0);
    const totalFallback = job?.total ?? processedFallback;

    return (
        <div className="mx-auto mt-4 w-full px-4">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* LEFT: Upload Form */}
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <h1 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
                        Upload Hype Train Prizes (Excel)
                    </h1>

                    <div className="relative mb-4">
                        <div
                            className={clsx(
                                "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors duration-150",
                                file
                                    ? "border-brand bg-brand/10 hover:bg-brand/20 dark:bg-brand/10 dark:hover:bg-brand/20"
                                    : "border-slate-300 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800",
                            )}
                            onClick={handleClick}
                        >
                            <FiUploadCloud className="h-10 w-10 text-slate-600 dark:text-slate-300" />
                            <p className="text-sm text-slate-700 dark:text-slate-400">
                                {file
                                    ? file.name
                                    : "Click to select an Excel file (.xlsx) containing hype train prizes"}
                            </p>
                        </div>

                        {file && (
                            <button
                                onClick={clearFileInput}
                                className="absolute top-2 right-2 cursor-pointer text-sm text-slate-600 hover:text-red-500 dark:text-slate-300"
                                title="Clear file"
                                type="button"
                            >
                                ×
                            </button>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            className="hidden"
                        />
                    </div>

                    {/* Delete existing checkbox */}
                    <div className="mb-4">
                        <label className="flex cursor-pointer items-center gap-2">
                            <input
                                type="checkbox"
                                checked={deleteExisting}
                                onChange={(e) =>
                                    setDeleteExisting(e.target.checked)
                                }
                                className="text-brand focus:ring-brand h-4 w-4 rounded border-slate-300"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-300">
                                Delete all existing prizes before importing
                            </span>
                        </label>
                        {deleteExisting && (
                            <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                                Warning: This will permanently delete all
                                existing hype train prizes!
                            </p>
                        )}
                    </div>

                    <button
                        disabled={!file || uploading}
                        onClick={handleUpload}
                        className={clsx(
                            "w-full rounded-md px-4 py-2 font-medium transition-colors",
                            uploading
                                ? "cursor-not-allowed bg-slate-300 text-slate-500 dark:bg-slate-800 dark:text-slate-500"
                                : "bg-brand hover:bg-brand/90 cursor-pointer text-white",
                        )}
                    >
                        {uploading ? "Uploading..." : "Upload"}
                    </button>

                    {/* Upload progress */}
                    {uploading && (
                        <div className="mt-3 h-2 w-full rounded bg-slate-200 dark:bg-slate-700">
                            <div
                                className="bg-brand h-2 rounded"
                                style={{ width: `${uploadPct}%` }}
                            />
                        </div>
                    )}

                    {/* Processing progress */}
                    {taskId && (
                        <div className="mt-4">
                            <p className="mb-2 text-sm text-slate-700 dark:text-slate-300">
                                Processing Excel{" "}
                                {job ? `— ${job.percent ?? 0}%` : ""}
                            </p>
                            <div className="h-2 w-full rounded bg-slate-200 dark:bg-slate-700">
                                <div
                                    className="bg-brand h-2 rounded"
                                    style={{ width: `${processingPct}%` }}
                                />
                            </div>
                            {job && (
                                <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                                    {processedFallback}/{totalFallback}{" "}
                                    processed • ok {job.successful ?? 0} •
                                    failed {job.failed ?? 0} • skipped{" "}
                                    {job.result?.skipped?.length ?? 0}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Results */}
                    {finished && job?.result && (
                        <div className="mt-4 space-y-3">
                            {job.result.deleted_count > 0 && (
                                <div className="rounded-lg bg-blue-100 p-4 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                                    <p className="mb-1 font-semibold">
                                        Cleanup
                                    </p>
                                    <p className="text-sm">
                                        Deleted {job.result.deleted_count}{" "}
                                        existing prizes before import.
                                    </p>
                                </div>
                            )}
                            {job.result.successful?.length > 0 && (
                                <div className="rounded-lg bg-green-100 p-4 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                                    <p className="mb-1 font-semibold">
                                        Success
                                    </p>
                                    <p className="text-sm">
                                        Imported {job.result.successful.length}{" "}
                                        prizes successfully.
                                    </p>
                                </div>
                            )}
                            {job.result.skipped?.length > 0 && (
                                <div className="rounded-lg bg-yellow-100 p-4 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                                    <p className="mb-1 font-semibold">
                                        Skipped
                                    </p>
                                    <p className="text-sm">
                                        Skipped {job.result.skipped.length}{" "}
                                        rows:{" "}
                                        {job.result.skipped
                                            .slice(0, 5)
                                            .join(", ")}
                                        {job.result.skipped.length > 5 && "..."}
                                    </p>
                                </div>
                            )}
                            {job.result.failed?.length > 0 && (
                                <div className="rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                                    <p className="mb-1 font-semibold">Failed</p>
                                    <p className="text-sm">
                                        Failed to import{" "}
                                        {job.result.failed.length} rows:{" "}
                                        {job.result.failed
                                            .slice(0, 5)
                                            .join(", ")}
                                        {job.result.failed.length > 5 && "..."}
                                    </p>
                                </div>
                            )}
                            {job.result.error && (
                                <div className="rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                                    <p className="mb-1 font-semibold">Error</p>
                                    <p className="text-sm">
                                        {job.result.error}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* RIGHT: Docs Panel */}
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
                        Excel File Format
                    </h2>
                    <p className="mb-4 text-sm leading-relaxed text-slate-700 dark:text-slate-400">
                        Your Excel file must have a header row with all of these
                        columns (all columns are required):
                    </p>
                    <table className="mb-4 w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                <th className="py-2 text-left font-semibold text-slate-900 dark:text-slate-100">
                                    Column
                                </th>
                                <th className="py-2 text-left font-semibold text-slate-900 dark:text-slate-100">
                                    Description
                                </th>
                            </tr>
                        </thead>
                        <tbody className="text-slate-700 dark:text-slate-400">
                            <tr className="border-b border-slate-100 dark:border-slate-800">
                                <td className="py-2 font-mono">rarity</td>
                                <td className="py-2">
                                    common, uncommon, rare, epic, fabled, aesir
                                    (defaults to common if empty)
                                </td>
                            </tr>
                            <tr className="border-b border-slate-100 dark:border-slate-800">
                                <td className="py-2 font-mono">message</td>
                                <td className="py-2">
                                    Prize description/message (rows without this
                                    will be skipped)
                                </td>
                            </tr>
                            <tr className="border-b border-slate-100 dark:border-slate-800">
                                <td className="py-2 font-mono">vp_reward</td>
                                <td className="py-2">
                                    VP points reward (defaults to 0)
                                </td>
                            </tr>
                            <tr className="border-b border-slate-100 dark:border-slate-800">
                                <td className="py-2 font-mono">bux_reward</td>
                                <td className="py-2">
                                    Bux currency reward (defaults to 0)
                                </td>
                            </tr>
                            <tr className="border-b border-slate-100 dark:border-slate-800">
                                <td className="py-2 font-mono">
                                    woh_token_reward
                                </td>
                                <td className="py-2">
                                    Wheel of Hamingja token (true/false,
                                    defaults to false)
                                </td>
                            </tr>
                            <tr>
                                <td className="py-2 font-mono">
                                    exalted_token_reward
                                </td>
                                <td className="py-2">
                                    Exalted token (true/false, defaults to
                                    false)
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p className="text-sm text-slate-600 dark:text-slate-500">
                        Rows without a valid <code>message</code> value will be
                        skipped.
                    </p>
                </div>
            </div>
        </div>
    );
}
